import React, { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
// Hooks
import { useAppDispatch } from 'hooks/redux';
// Async
import PaymentsAsync from 'store/payments/paymentsAsync';
// Actions
import { reservationsActions } from 'store/reservations/reservationsSlice';
// Mui
import {
  Button, DialogActions, DialogContent, TextField,
  Grid, Box, Typography, Checkbox, MenuItem
} from '@mui/material';
import {
  LoadingButton
} from '@mui/lab';
// Utilities
import { isFieldRequired } from 'utilities/Validation';
// Credit-card
import Cards from 'react-credit-cards';
import 'react-credit-cards/es/styles-compiled.css';
import dayjs from 'dayjs';

interface IFormData {
  cardNumber: string;
  expMonth: number | string;
  expYear: number | string;
  cvv: string;
}

type Props = {
  reservationId: number,
  onClose: () => void,
}

const PaymentCreditCardForm:React.FC<Props> = ({ reservationId, onClose }) => {
  const dispatch = useAppDispatch();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [accepted, setAccepted] = useState<boolean>(false);
  const [cardFocused, setCardFocused] = useState<any>('number');

  const { control, handleSubmit, formState:{ errors }, watch } = useForm<IFormData>({
    defaultValues: {
      cardNumber: '',
      expMonth: '',
      expYear: '',
      cvv: '',
    }
  });

  const cardNumberWatcher = watch('cardNumber');
  const expMonthWatcher = watch('expMonth');
  const expYearWatcher = watch('expYear');
  const cvvWatcher = watch('cvv');

  const handleCardFocused = (e: any) => {
    const { name } = e.target;
    setCardFocused(name);
  }

  const onSubmit = handleSubmit((data:IFormData) => {
    setIsLoading(true);
  
    const { expMonth, expYear, ...nextData } = data;
    const newData = {
      ...nextData,
      expMonth: +expMonth,
      expYear: +expYear,
    }
    const reservationPaymentData = {
      reservationId,
      iroquoisCreditCardPaymentData: {...newData},
    }
    dispatch(PaymentsAsync.createPayment({reservationPaymentData}))
      .unwrap()
      .then(() => dispatch(reservationsActions.markAsPaidReservation(reservationId)))
      .finally(() => setIsLoading(false));
  });

  return (
    <React.Fragment>
      <DialogContent>
        <Cards
          cvc={cvvWatcher}
          expiry={`${expMonthWatcher}/${expYearWatcher}`}
          focused={cardFocused}
          name=" "
          number={cardNumberWatcher}
        />
        <form onSubmit={onSubmit} noValidate>
          <Grid container spacing={2} sx={{ pt: 5 }}>
            <Grid item xs={12}>
              {/* cardNumber */}
              <Controller
                control={control} name="cardNumber"
                rules={{ required: isFieldRequired }}
                render={({ field: { onChange, value } }) => (
                  <TextField
                    size="small"
                    value={value.split('').map((item, index) => (index + 1) % 4 === 0 ? `${item} ` : item).join('').trim()}
                    onChange={(e:any) => onChange(e.target.value.replace(/\D/g, '').slice(0,16))}
                    onFocus={handleCardFocused}
                    fullWidth
                    name="number"
                    label="Card number"
                    placeholder="XXXX XXXX XXXX XXXX"
                    type="text"
                    error={Boolean(errors.cardNumber)}
                    helperText={errors.cardNumber ? errors.cardNumber.message : ''}
                    required
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              {/* expMonth */}
              <Controller
                control={control} name="expMonth"
                rules={{ required: isFieldRequired }}
                render={({ field }) => (
                  <TextField
                    select
                    {...field}
                    onFocus={handleCardFocused}
                    size="small"
                    fullWidth
                    name="expiry"
                    label="Exp Month"
                    error={Boolean(errors.expMonth)}
                    helperText={errors.expMonth ? errors.expMonth.message : ''}
                    required
                  >
                    {Array.from({ length: 12 }, (_:any, index:number) => dayjs().month(index).format('MM')).map((month) => (
                      <MenuItem key={month} value={month}>{month}</MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              {/* expYear */}
              <Controller
                control={control} name="expYear"
                rules={{ required: isFieldRequired }}
                render={({ field }) => (
                  <TextField
                    select
                    {...field}
                    onFocus={handleCardFocused}
                    size="small"
                    fullWidth
                    name="expiry"
                    label="Exp Year"
                    error={Boolean(errors.expYear)}
                    helperText={errors.expYear ? errors.expYear.message : ''}
                    required
                  >
                    {Array.from({ length: 21 }, (_:any, index:number) => dayjs().add(index, 'year').format('YYYY')).map((year) => (
                      <MenuItem key={year} value={year}>{year}</MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              {/* cvv */}
              <Controller
                control={control} name="cvv"
                rules={{ required: isFieldRequired }}
                render={({ field: { onChange, value } }) => (
                  <TextField
                    value={value}
                    onChange={(e:any) => onChange(e.target.value.slice(0,3))}
                    onFocus={handleCardFocused}
                    size="small"
                    fullWidth
                    name="cvc"
                    label="CVV"
                    placeholder="XXX"
                    type="text"
                    error={Boolean(errors.cvv)}
                    helperText={errors.cvv ? errors.cvv.message : ''}
                    required
                  />
                )}
              />
            </Grid>
          </Grid>
        </form>
      </DialogContent>
      <Box sx={{ height: '200px', overflowY: 'scroll', m: 2 }}>
        <Typography sx={{ fontSize: '24px', fontWeight: 600 }}>
          PAYMENT TERMS AND CONDITIONS
        </Typography>
        <Typography sx={{ fontSize: '18px', fontWeight: 500 }}>
          AUTHORIZATION
        </Typography>
        <Typography sx={{ fontSize: '14px' }}>
          By checking the "I accept the Terms and Conditions" checkbox below I authorize my bank to debit my specified account for the amount of my payment. This is a one-time payment which will occur on the next business day or as soon as practical thereafter. If my payment cannot be completed for any reason, including insufficient funds or error in the information which I submitted, I will retain the same liability, which is my sole responsibility, for payment as though I had not attempted to make the payment. I also understand that additional fees and penalties may be collected to the extent of applicable law.This transaction may not be disputed and is non-refundable. Dates cannot be altered.
        </Typography>
        <Typography sx={{ fontSize: '18px', fontWeight: 500 }}>
          RECEIPT
        </Typography>
        <Typography sx={{ fontSize: '14px' }}>
          A receipt can be printed after payment is accepted which may serve as evidence of payment. If an email address is provided during the payment process, a receipt will be emailed to you after the payment is processed.
        </Typography>
        <Typography sx={{ fontSize: '18px', fontWeight: 500 }}>
          CONTACT
        </Typography>
        <Typography sx={{ fontSize: '14px' }}>
          If for any reason you wish to make a change to the payment after submission, please contact Dash Realty Group as the first option for assistance at 407-913-3274. Dashweek is a brand of Dash Realty Group. It is possible that you may receive correspondence from Dash Realty Group representative. I agree to the terms and conditions.
        </Typography>
      </Box>
      <DialogActions>
        <label style={{ display: 'flex', alignItems: 'center' }}>
          <Checkbox  checked={accepted} onChange={(_:any, checked: boolean) => setAccepted(checked)} />
          <Typography sx={{ fontSize: '14px' }}>
              I ACCEPT THE TERMS AND CONDITIONS
          </Typography>
        </label>
        <Box flexGrow="1" />
        <Box sx={{ minWidth: 'max-content' }}>
          <Button
            variant="text"
            color="primary"
            onClick={onClose}
          >
            Cancel
          </Button>
          <LoadingButton
            type="submit"
            loading={isLoading}
            onClick={onSubmit}
            variant="contained"
            color="primary"
            disabled={!accepted}
          >
            Pay
          </LoadingButton>
        </Box>
      </DialogActions>
    </React.Fragment>
  )
}

export default PaymentCreditCardForm;
